import {
  ColDef,
  FilterModel,
  GetRowIdParams,
  GridApi,
  GridOptions,
  RowDataUpdatedEvent,
  SideBarDef,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the grid
import "ag-grid-community/styles/ag-theme-balham.css"; // Optional Theme applied to the grid
import "ag-grid-community/styles/ag-theme-quartz.css"; // Optional Theme applied to the grid
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import PriceTableDetail from "./PriceTableDetail";

// custom helper functions:
import {
  debounce,
  getColumnWidthMultiplier,
  getFontSizePx,
  getHeaderHeight,
  getRowHeight,
} from "@/components/widgets/tradeActionSummary/helper/actionSummaryHelper";
import AGGridNoRowsOverlay from "@/components/UI/AGGridNoRowsOverlay";
import {
  AG_GRID_LOADING_TEMPLATE,
  syncNoRowsOverlay,
} from "@/lib/agGridOverlayHelper";
import { gridHasLeafRows } from "@/lib/agGridFilterHelper";
import { returnOpenWidgetsObject } from "@/lib/widgetHelperFunctions";
import bondSecuritiesStore from "@/store/bondSecuritiesStore";
import priceDataStore, { IPriceDataItem } from "@/store/priceDataStore";
import priceTabStore, {
  defaultConfigTab,
  IPriceConfigTab,
} from "@/store/pricesTabStore";
import { updateConfigJSONPriceConfigInStoreAndAPI } from "@/store/pricesTabStoreACTIONS";
import {
  getContextMenuItemsPriceTable,
  isPriceReferenceDataLoading,
  mainPriceGridStyles,
  priceTableColDefs,
  retunGetMainMenuItems,
  returnActiveConfigIdArrForWidget,
  returnColumnStateToSave,
  returnConfigJsonForGrid,
  returnRowGroupStateToApply,
  returnPriceTableSidebarObj,
  shouldPersistFiltersToConfig,
} from "./helper/priceTableHelper";
import PricesSidebarContent from "./PricesSidebarContent";
import { viewsRailIcon } from "@/components/icons/SVGIcons";

// Module scope so the grid gets a stable reference.
const PRICE_SIDEBAR_ICONS = { views: viewsRailIcon };

import useTableUIStore from "@/store/tableUIStore";
import agGridFlashNotifications from "@/store/notifications/agGridFlashNotifications";
interface IPriceTable {
  activePricesConfigIdNumber: number;
  getAccessTokenSilently: Function;
  cryptoID?: string | undefined;
  settingsConfigsFromStore: IPriceConfigTab[];
  theme: any;
  setActivePricesConfigIdNumber: Function;
}

const PriceTable = ({
  activePricesConfigIdNumber,
  getAccessTokenSilently,
  cryptoID,
  settingsConfigsFromStore,
  theme,
  setActivePricesConfigIdNumber,
}: IPriceTable) => {
  const priceStoreForRowData = priceDataStore((state) => state.data);
  // LQC-2575 - "No rows to show" must mean the fetch came back empty, so while
  // GET /Securities/bonds is still in flight the grid gets undefined rowData and
  // shows the loading overlay instead. Idle counts as loading: PriceIndex asks for
  // the data on mount, so Idle is the tick before the request goes out.
  const bondSecuritiesStatus = bondSecuritiesStore(
    (state) => state.bondSecuritiesStatus,
  );
  const isReferenceDataLoading =
    isPriceReferenceDataLoading(bondSecuritiesStatus);
  const gridRef = useRef<AgGridReact>(null);
  const [rowData, setRowData] =
    useState<IPriceDataItem[]>(priceStoreForRowData);
  const [onGridReadyFinished, setOnGridReadyFinished] = useState(false);
  const [isAnyFilterActive, setIsAnyFilterActive] = useState(false);
  const [colDefs] = useState<ColDef[]>(priceTableColDefs);
  // Which config's column state the grid is showing, so a shared-store write does not
  // count as a config switch. null until the first restore runs.
  const appliedConfigIdRef = useRef<number | null>(null);
  // The configJson this grid last applied, so the widget that wrote a change does not
  // then re-apply its own write over what the user is still doing.
  const appliedConfigJsonRef = useRef<string | null>(null);
  // True while a shared-config change is being pushed into this grid.
  const applyingSharedConfigRef = useRef(false);
  // A config's filter waiting for rows (LQC-3117 - Issuer/Currency are Set Filters).
  const pendingFilterModelRef = useRef<FilterModel | null>(null);

  // Set Filters restored before the rows land lose their values, so hold the model
  // until onRowDataUpdated finds rows. Clearing (null) never needs to wait.
  const applyConfigFilterModel = (api: GridApi, model?: FilterModel | null) => {
    if (model && !gridHasLeafRows(api)) {
      pendingFilterModelRef.current = model;
      return;
    }
    pendingFilterModelRef.current = null;
    api.setFilterModel(model ?? null);
  };

  const onRowDataUpdated = (params: RowDataUpdatedEvent) => {
    const pendingModel = pendingFilterModelRef.current;
    if (!pendingModel || !gridHasLeafRows(params.api)) return;
    pendingFilterModelRef.current = null;
    // Restoring the saved filter is not a user edit, so it must not be saved back.
    applyingSharedConfigRef.current = true;
    params.api.setFilterModel(pendingModel);
    setTimeout(() => {
      applyingSharedConfigRef.current = false;
    }, 0);
  };

  //Odd row stripe
  const tableStripeOddTrue = useTableUIStore(
    (state) => state.tableStripePricesOn,
  ); // used to change the table stripe with settings.
  const fontSize = useTableUIStore((state) => state.fontSize);
  const rowHeight = useTableUIStore((state) => state.rowHeight);
  const pricesColorLight = useTableUIStore((state) => state.pricesColorLight);
  const pricesColorDark = useTableUIStore((state) => state.pricesColorDark);
  const pricesFlashColorLight = useTableUIStore(
    (state) => state.pricesFlashColorLight,
  );
  const pricesFlashColorDark = useTableUIStore(
    (state) => state.pricesFlashColorDark,
  );
  const testFlashPrices = useTableUIStore(
    (state) => state.testFlashPrices,
  );
  const storeRowFlashDuration = agGridFlashNotifications(
    (state) => state.pricesFlashDuration,
  );

  // Listen for changes in active config id and add to the store.
  useEffect(() => {
    priceTabStore
      .getState()
      .setActivePricesConfigIdNumberStoreArr(
        returnActiveConfigIdArrForWidget(
          priceTabStore.getState().activePricesConfigIdNumberStoreArr,
          cryptoID,
          activePricesConfigIdNumber,
        ),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePricesConfigIdNumber]);

  useEffect(() => {
    setRowData(priceStoreForRowData);
  }, [priceStoreForRowData]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      enableCellChangeFlash: true,
      // LQC-2575 - matches the blotter: one filter funnel per header, no hamburger.
      suppressHeaderMenuButton: true,
      cellStyle: { borderRight: "Var(--ag-grid-cubeCustom-cell-border)" },
    };
  }, []);

  const autoGroupColumnDef = useMemo<ColDef>(() => {
    return {
      minWidth: 130,
    };
  }, []);

  // LQC-2575 - a config is a named, SHARED view and owns BOTH its columns and its
  // filter, so "UST" carries the UST filter. Default is the unnamed scratch sheet:
  // its filter is never saved, making it throwaway per session and per widget.
  const saveStateToActiveConfig = useCallback(() => {
    if (!gridRef.current) return;

    const newConfigJson = returnConfigJsonForGrid(
      returnColumnStateToSave(gridRef.current.api.getColumnState()),
      // A filter still waiting for rows is not in the grid yet - save it, not {}.
      pendingFilterModelRef.current ?? gridRef.current.api.getFilterModel(),
      activePricesConfigIdNumber,
    );
    appliedConfigJsonRef.current = newConfigJson;

    const currentConfigs = priceTabStore.getState().priceConfigTabs;
    const activeConfig = currentConfigs.find(
      (config) => config.id == activePricesConfigIdNumber,
    );
    if (!activeConfig) return;

    // Never mutate the config in the store - a new object is what tells React the
    // config changed, and mutating it made a stale read look up to date.
    const updatedConfig = { ...activeConfig, configJson: newConfigJson };

    // The Default config is local only, so it is set in the store without a PUT.
    if (activePricesConfigIdNumber == defaultConfigTab.id) {
      priceTabStore
        .getState()
        .setPriceConfigTabs(
          currentConfigs.map((config) =>
            config.id == defaultConfigTab.id ? updatedConfig : config,
          ),
        );
      return;
    }

    updateConfigJSONPriceConfigInStoreAndAPI({
      configToSendViaAPI: updatedConfig,
      getAccessTokenSilently,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePricesConfigIdNumber, getAccessTokenSilently]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSaveGridState = useCallback(
    debounce(saveStateToActiveConfig, 400),
    [saveStateToActiveConfig],
  );

  useEffect(
    () => {
      const gridInstance = gridRef.current?.api;

      const configFromStoreMatchingId = settingsConfigsFromStore.find(
        (config) => config.id == activePricesConfigIdNumber,
      );
      const switchedConfig =
        appliedConfigIdRef.current != activePricesConfigIdNumber;
      // Someone else edited the config this widget is showing. Never true for the
      // widget that made the edit - it records what it wrote (see the save).
      const configContentChanged =
        !switchedConfig &&
        appliedConfigJsonRef.current != configFromStoreMatchingId?.configJson;

      if (gridInstance && (switchedConfig || configContentChanged)) {
        appliedConfigIdRef.current = activePricesConfigIdNumber;
        appliedConfigJsonRef.current =
          configFromStoreMatchingId?.configJson ?? null;

        const configAsObject =
          configFromStoreMatchingId && configFromStoreMatchingId.configJson != ""
            ? JSON.parse(configFromStoreMatchingId.configJson)
            : null;

        // Applying this fires filterChanged, which must not be saved back - two
        // widgets would then write to each other in a loop.
        applyingSharedConfigRef.current = true;

        if (switchedConfig) {
          if (configAsObject) {
            gridInstance.applyColumnState({
              state: configAsObject.columnState,
              applyOrder: true,
            });
          } else {
            // A brand new config has no saved state yet, so it starts as default.
            gridInstance.resetColumnState();
          }
        } else if (configAsObject) {
          // Same config, edited elsewhere: take the row grouping but leave this
          // widget's own column widths and order alone.
          gridInstance.applyColumnState({
            state: returnRowGroupStateToApply(configAsObject.columnState),
          });
        }

        // LQC-2575 - two widgets on one named config are one view, so a filter set in
        // either shows in both. Default saves no filter, so switching to it lands
        // unfiltered rather than carrying the previous config's filter across.
        applyConfigFilterModel(gridInstance, configAsObject?.filterModel);

        setTimeout(() => {
          applyingSharedConfigRef.current = false;
        }, 0);
      }
      // A change this widget is applying from the shared config must not be saved
      // back - two widgets would then write to each other in a loop.
      const onColumnChangeNoticed = () => {
        if (applyingSharedConfigRef.current) return;
        debouncedSaveGridState();
      };
      // Nothing to persist on Default - its filter is deliberately throwaway - and
      // nothing to persist when this change came from another widget's save.
      const onFilterChangeNoticed = () => {
        if (applyingSharedConfigRef.current) return;
        if (!shouldPersistFiltersToConfig(activePricesConfigIdNumber)) return;
        debouncedSaveGridState();
      };

      if (gridInstance) {
        gridInstance.addEventListener("columnResized", onColumnChangeNoticed);
        gridInstance.addEventListener("sortChanged", onColumnChangeNoticed);
        gridInstance.addEventListener("columnVisible", onColumnChangeNoticed);
        gridInstance.addEventListener("columnMoved", onColumnChangeNoticed);
        // Row grouping was never persisted at all before LQC-2575.
        gridInstance.addEventListener(
          "columnRowGroupChanged",
          onColumnChangeNoticed,
        );
        gridInstance.addEventListener("filterChanged", onFilterChangeNoticed);
      }

        return () => {
        if (gridInstance) {
          gridInstance.removeEventListener(
            "columnResized",
            onColumnChangeNoticed,
          );
          gridInstance.removeEventListener(
            "sortChanged",
            onColumnChangeNoticed,
          );
          gridInstance.removeEventListener(
            "columnVisible",
            onColumnChangeNoticed,
          );
          gridInstance.removeEventListener(
            "filterChanged",
            onFilterChangeNoticed,
          );
          gridInstance.removeEventListener(
            "columnMoved",
            onColumnChangeNoticed,
          );
          gridInstance.removeEventListener(
            "columnRowGroupChanged",
            onColumnChangeNoticed,
          );
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activePricesConfigIdNumber, onGridReadyFinished, settingsConfigsFromStore],
  );

  const onGridReady = () => {
    const openWidgetObject = returnOpenWidgetsObject(cryptoID);

    // Columns and filter both come from the config this widget has active. Default
    // saves neither filter nor a PUT, so it simply starts clean.
    if (
      gridRef.current &&
      openWidgetObject.activePricesConfigIdNumber != defaultConfigTab.id
    ) {
      const configMatchingSavedState = settingsConfigsFromStore.find(
        (config) => config.id == openWidgetObject.activePricesConfigIdNumber,
      );

      if (
        configMatchingSavedState &&
        configMatchingSavedState.configJson != ""
      ) {
        const configAsObject = JSON.parse(configMatchingSavedState.configJson);
        gridRef.current.api.applyColumnState({
          state: configAsObject.columnState,
          applyOrder: true,
        });
        applyConfigFilterModel(gridRef.current.api, configAsObject.filterModel);
        appliedConfigIdRef.current =
          openWidgetObject.activePricesConfigIdNumber;
        appliedConfigJsonRef.current = configMatchingSavedState.configJson;
      }
    }

    setRowData(priceStoreForRowData);
    setOnGridReadyFinished(true);
  };

  const getRowId = useCallback((params: GetRowIdParams) => {
    return params.data.id;
  }, []);

  const onCellValueChanged = useCallback(() => {}, []);

  // Mirror the header's orange active-filter state onto the sidebar tab.
  function handlePriceFilterChange(event: any) {
    setIsAnyFilterActive(event.api.isAnyFilterPresent());
  }

  const gridOptions: GridOptions = {
    defaultColDef,
    autoGroupColumnDef,
    getRowId,
    reactiveCustomComponents: true,

    getMainMenuItems: (params) => {
      return retunGetMainMenuItems(params);
    },
    getContextMenuItems: () => {
      return getContextMenuItemsPriceTable();
    },
  };

  const detailCellRenderer = useMemo(() => {
    return PriceTableDetail;
  }, []);

  const PriceTableSidebar: SideBarDef = useMemo(() => {
    return returnPriceTableSidebarObj(
      activePricesConfigIdNumber,
      setActivePricesConfigIdNumber,
      cryptoID,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const style = useMemo(
    () => {
      const baseStyles = mainPriceGridStyles(
        theme,
        tableStripeOddTrue,
      ) as React.CSSProperties;
      return {
        ...baseStyles,
        fontSize: `${getFontSizePx(fontSize)}px`,
      };
    },
    [
      theme,
      tableStripeOddTrue,
      fontSize,
      pricesColorLight,
      pricesColorDark,
      pricesFlashColorLight,
      pricesFlashColorDark,
    ],
  );

  // Test flash from settings — flash the top row when triggered
  useEffect(() => {
    if (testFlashPrices > 0 && gridRef.current?.api) {
      const firstRow = gridRef.current.api.getDisplayedRowAtIndex(0);
      if (firstRow) {
        gridRef.current.api.flashCells({
          rowNodes: [firstRow],
          flashDuration: storeRowFlashDuration,
          fadeDuration: 1000,
        });
      }
    }
  }, [testFlashPrices]);

  const dynamicRowHeight = useMemo(
    () => getRowHeight(rowHeight),
    [rowHeight],
  );

  const dynamicHeaderHeight = useMemo(
    () => getHeaderHeight(rowHeight),
    [rowHeight],
  );

  const columnWidthMultiplier = useMemo(
    () => getColumnWidthMultiplier(fontSize),
    [fontSize],
  );

  const popupParent = useMemo<HTMLElement | null>(() => {
    return document.querySelector(".GridContainer");
  }, []);

  // Effect to scale column widths when font size changes
  useEffect(() => {
    if (gridRef.current?.api) {
      const allColumns = gridRef.current.api.getColumns();
      if (allColumns) {
        allColumns.forEach((column) => {
          const colDef = column.getColDef() as any;
          if (colDef.fixedWidth) return; // Skip fixed-width control columns
          if (colDef.width) {
            const baseWidth = colDef.width;
            const newWidth = Math.round(baseWidth * columnWidthMultiplier);
            gridRef.current?.api.setColumnWidth(column, newWidth);
          }
          if (colDef.minWidth) {
            const baseMinWidth = colDef.minWidth;
            const newMinWidth = Math.round(baseMinWidth * columnWidthMultiplier);
            column.getColDef().minWidth = newMinWidth;
          }
        });
      }
    }
  }, [fontSize, columnWidthMultiplier]);

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex w-full flex-grow">
        <div
          className={`${theme == "dark" ? "ag-theme-balham-dark" : "ag-theme-balham"} ${isAnyFilterActive ? "filterActiveSideBar" : ""} h-full w-full border-l-2 border-t border-[#E7EAEB] dark:border-t-2 dark:border-[#252525]`}
          style={style}
        >
          <AgGridReact
            ref={gridRef}
            rowData={isReferenceDataLoading ? undefined : rowData}
            overlayLoadingTemplate={AG_GRID_LOADING_TEMPLATE}
            columnDefs={colDefs}
            columnMenu={"new"} //do not delete, affects header menus - allows the filter icon to be visible
            onFilterChanged={handlePriceFilterChange}
            gridOptions={gridOptions}
            defaultColDef={defaultColDef}
            autoGroupColumnDef={autoGroupColumnDef}
            onGridReady={onGridReady}
            onRowDataUpdated={onRowDataUpdated}
            // LQC-2343: shared sentence-case overlay. AG Grid 31 shows nothing
            // when a filter leaves 0 rows, so sync manually (see LQC-2482).
            onModelUpdated={(params) =>
              syncNoRowsOverlay(params.api, isReferenceDataLoading)
            }
            noRowsOverlayComponent={AGGridNoRowsOverlay}
            suppressAggFuncInHeader={true}
            valueCache={true}
            onCellValueChanged={onCellValueChanged}
            masterDetail={true}
            detailCellRenderer={detailCellRenderer}
            // detailCellRendererParams={{ theme }}
            detailRowAutoHeight={true}
            // LQC-2575 - the drop zone that lets a user group by issuer / currency.
            rowGroupPanelShow={"always"}
            groupDisplayType={"groupRows"}
            sideBar={PriceTableSidebar}
            scrollbarWidth={6}
            components={{
              customConfigsToolPanel: PricesSidebarContent,
            }}
            icons={PRICE_SIDEBAR_ICONS}
            rowHeight={dynamicRowHeight}
            headerHeight={dynamicHeaderHeight}
            popupParent={popupParent}
          />
        </div>
      </div>
    </div>
  );
};

export default PriceTable;
